import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { useAuthUser } from "@/context/auth/AuthContext";
import { sePuedeCargarElPin } from "@/context/caja/cajeroDeLaCaja";
import { useEstadoDeLaCaja } from "@/context/caja/estadoDeLaCaja";
import {
  AVISO_COLA_APARTADA,
  useFotoDeLaCaja,
} from "@/context/caja/fotoDeLaCaja";
import { localDeLaCaja } from "@/context/caja/localDeLaCaja";
import {
  almacenNoDisponible,
  cajerosConPin,
  filasDeLaFoto,
  lineaDeLaConexion,
  resumenDeLaFoto,
  ventaSinConexion,
  ventasSinSubir,
} from "@/context/caja/panelDeLaCaja";
import { situacionDelEquipo } from "@/context/caja/situacionDelEquipo";
import { porQueNoVendeSinRed } from "@/context/caja/ventaSinRed";
import { useSinRedDelNavegador } from "@/hooks/caja/useSinRedDelNavegador";
import { useAjustesDeLaFoto } from "@/hooks/react-query/Caja/useAjustesDeLaFoto";
import { useMandatoDeLaCaja } from "@/hooks/react-query/Caja/useMandatoDeLaCaja";
import { useTerminalDelEquipo } from "@/hooks/react-query/Caja/useTerminalDelEquipo";
import { useWarehouseName } from "@/hooks/useWarehouseName";

function Seccion({
  titulo,
  children,
}: {
  titulo: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-1">
      <h3 className="text-xs font-semibold uppercase text-text-secondary">
        {titulo}
      </h3>
      {children}
    </section>
  );
}

/**
 * El estado de la caja de este equipo, desde la barra (#344 AC-2, #345 AC-7,
 * #346): la conexión, lo que falta subir, la foto parte por parte, si vende sin
 * conexión y por qué no, y quiénes entran con PIN. Lo que todavía no está se
 * dice, en vez de no mostrarse.
 */
export function PanelDeLaCaja({
  onIr,
  onCargarPin,
}: {
  onIr?: () => void;
  /** Abre «Cargar mi PIN»: el diálogo vive afuera, el panel se cierra. */
  onCargarPin?: () => void;
}) {
  const navigate = useNavigate();
  const user = useAuthUser();
  const businessId = user?.businessId as string | undefined;
  const estado = useEstadoDeLaCaja();
  const foto = useFotoDeLaCaja((s) => s.foto);
  const almacen = useFotoDeLaCaja((s) => s.almacen);
  const { terminal } = useTerminalDelEquipo();
  const mandato = useMandatoDeLaCaja(businessId);
  const ajustes = useAjustesDeLaFoto(businessId);
  // Con red, el nombre del local sale de la lista; sin red, de la foto.
  const nombreDelLocal = useWarehouseName(terminal?.warehouseId);
  const sinRedDelNavegador = useSinRedDelNavegador();

  // Sigue ahí hasta que soporte retire el archivo: sobrevive a un reinicio.
  const apartada = almacen?.colaApartada ? (
    <Seccion titulo="La cola de la caja quedó apartada">
      <p className="text-error-foreground">{AVISO_COLA_APARTADA}</p>
    </Seccion>
  ) : null;

  if (almacen && !almacen.disponible) {
    const problema = almacenNoDisponible(
      almacen.error,
      estado.conexion === "sin-conexion" || sinRedDelNavegador
    );
    return (
      <div className="flex flex-col gap-2 text-sm">
        <p className="font-semibold">{problema.titulo}</p>
        <p>{problema.mensaje}</p>
        {problema.detalle && (
          <p className="text-xs text-text-secondary">{problema.detalle}</p>
        )}
      </div>
    );
  }

  if (!terminal) {
    return (
      <div className="flex flex-col gap-3 text-sm">
        {apartada}
        <p>
          {apartada
            ? "Este equipo no es una caja hasta que se dé de alta de nuevo."
            : "La caja está abriendo sus datos. Si el sistema pide permiso para el llavero, poné tu contraseña y elegí «Permitir siempre»."}
        </p>
      </div>
    );
  }

  const tienePin = !!mandato.data?.cajerosConPin.some(
    (c) => c.userId === String(user?.id ?? "")
  );
  const puedeCargarPin = sePuedeCargarElPin(estado, user);
  const ir = (ruta: string) => {
    onIr?.();
    navigate(ruta);
  };
  const local = localDeLaCaja(terminal, {
    locales: nombreDelLocal
      ? [{ id: terminal.warehouseId, name: nombreDelLocal }]
      : null,
    foto: ajustes.data,
  });
  const venta = ventaSinConexion(
    porQueNoVendeSinRed({
      situacion: situacionDelEquipo(terminal, terminal.warehouseId).tipo,
      estado,
      foto,
      almacen,
      mandato: mandato.data,
    }),
    mandato.data
  );

  return (
    <div className="flex flex-col gap-4 text-sm">
      {apartada}
      <div>
        <p className="font-semibold">
          «{terminal.name}»{terminal.prefix ? ` · ${terminal.prefix}` : ""}
          {local?.nombre ? ` · ${local.nombre}` : ""}
        </p>
        <p className="text-text-secondary tabular-nums">
          {lineaDeLaConexion(estado)}
        </p>
      </div>

      <Seccion titulo="Ventas">
        <div className="flex items-center justify-between gap-2">
          <span>{ventasSinSubir(estado.pendientes)}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => ir("/ventas/caja/pendientes")}
          >
            Ver ventas de la caja
          </Button>
        </div>
      </Seccion>

      <Seccion titulo="Datos para vender sin conexión">
        <p>{resumenDeLaFoto(foto, estado.servidorConFoto)}</p>
        {/* En columnas: con la cantidad pegada a la fecha, cada fila la corría
            según cuántos dígitos tuviera. */}
        <table className="w-full tabular-nums">
          <tbody>
            {filasDeLaFoto(foto).map((fila) => (
              <tr key={fila.parte} className="align-top">
                <th
                  scope="row"
                  className="w-full py-0.5 pr-3 text-left font-normal"
                >
                  {fila.nombre}
                </th>
                <td className="whitespace-nowrap py-0.5 text-right text-text-secondary">
                  {fila.estado}
                  {fila.bajando && (
                    <span className="block text-xs">{fila.bajando}</span>
                  )}
                </td>
                <td className="whitespace-nowrap py-0.5 pl-3 text-right text-text-secondary">
                  {fila.cantidad}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Seccion>

      <Seccion titulo="Vender sin conexión">
        <p className="font-medium">{venta.titulo}</p>
        {venta.lineas.map((linea) => (
          <p key={linea} className="text-text-secondary">
            {linea}
          </p>
        ))}
      </Seccion>

      <Seccion titulo="Cajeros con PIN">
        {/* Como «Ventas»: el texto a la izquierda y su acción a la derecha. */}
        <div className="flex items-center justify-between gap-2">
          <span>{cajerosConPin(mandato.data)}</span>
          {puedeCargarPin && (
            <Button
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={() => onCargarPin?.()}
            >
              {tienePin ? "Cambiar mi PIN" : "Cargar mi PIN"}
            </Button>
          )}
        </div>
        {!puedeCargarPin && estado.mandatoId !== null && (
          <p className="text-xs text-text-secondary">
            Se carga con conexión y con tu sesión.
          </p>
        )}
      </Seccion>
    </div>
  );
}
